"use client";

import { Button } from "@mui/material";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useVariant, Variant } from "@/hooks/useVariant";
import { getAssetUrl } from "@/utils/assetUrl";

const WOMANS_SLEEP_ASSESSMENT_CAMPAIGN_URL = "https://www.resmed.com/en-us/campaigns/womens-sleep-assessment/";
const COPY_SUCCESS_ICON_DURATION_MS = 1100;

export default function RedefiningSleep() {
  const t = useTranslations("results-page.redefining-sleep");
  const variant = useVariant();
  const copyIconResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isCopySuccessIconVisible, setIsCopySuccessIconVisible] = useState(false);

  const genderCaptionPath: "woman" | "generic" = variant === Variant.FEMALE ? "woman" : "generic";

  useEffect(() => {
    return () => {
      if (copyIconResetTimeoutRef.current) {
        clearTimeout(copyIconResetTimeoutRef.current);
      }
    };
  }, []);

  const onCopyLink = () => {

    if (typeof window === "undefined" || !navigator.clipboard?.writeText) {
      return;
    }

    navigator.clipboard.writeText(WOMANS_SLEEP_ASSESSMENT_CAMPAIGN_URL)
    .catch(() => undefined)
    .then(() => {
      setIsCopySuccessIconVisible(true);

      if (copyIconResetTimeoutRef.current) {
        clearTimeout(copyIconResetTimeoutRef.current);
      }

      copyIconResetTimeoutRef.current = setTimeout(() => {
        setIsCopySuccessIconVisible(false);
        copyIconResetTimeoutRef.current = null;
      }, COPY_SUCCESS_ICON_DURATION_MS);
    });
  };

  return (
    <section className="redefining-sleep">
      <div className="redefining-sleep-noise" aria-hidden="true">
        <div className="redefining-sleep-gradient"></div>
        <Image
          src={getAssetUrl("/images/noise.svg")}
          alt=""
          className="redefining-sleep-noise-image"
          fill
          sizes="100vw"
          unoptimized
        />
      </div>
      <div className="redefining-sleep-content">
        <div className="redefining-sleep-headline-cta">
          <h2 className="headline redefining-sleep-headline">
            {t.rich(`${genderCaptionPath}.top-headline`, {
              bold: (chunks) => (
                <span className="redefining-sleep-headline-bold">{chunks}</span>
              ),
            })}
          </h2>
          <p className="redefining-sleep-subheadline">{t(`${genderCaptionPath}.bottom-headline`)}</p>
          <div
            className="redefining-sleep-copy-link-container"
          >
            <Button
            variant="contained"
            disableElevation
            disableRipple
            className="redefining-sleep-copy-link"
            onClick={onCopyLink}
          >
            {t("cta-copy-link")}
            {isCopySuccessIconVisible ? (
              <span
                className="redefining-sleep-copy-link-icon redefining-sleep-copy-link-icon--check"
                aria-hidden="true"
              >
                <svg
                  className="redefining-sleep-copy-link-checkmark"
                  viewBox="0 0 24 24"
                  focusable="false"
                >
                  <path d="M5.5 12.5L10 17L18.5 8.5" />
                </svg>
              </span>
            ) : (
              <Image
                src={getAssetUrl("/images/link_icon.svg")}
                alt=""
                className="redefining-sleep-copy-link-icon"
                width={24}
                height={24}
                unoptimized
              />
            )}
          </Button>
          </div>
        </div>

        <div className="redefining-sleep-image-stats">
          <div className="redefining-sleep-left-column">
            <div className="redefining-sleep-stats-card">
              <p className="redefining-sleep-stats-header">{t("stats-header")}</p>
              <p className="redefining-sleep-stats-body">{t("stats-body")}</p>
            </div>
            <div className="redefining-sleep-image-card redefining-sleep-image-card--large">
              <Image
                src={getAssetUrl("/images/results/redefining_sleep_completed_count.webp")}
                alt={t("image-alt-large")}
                className="redefining-sleep-image redefining-sleep-image-sleep-completed-count"
                width={352}
                height={406}
                unoptimized
              />
            </div>
          </div>

          <div className="redefining-sleep-right-column">
            <div className="redefining-sleep-image-card redefining-sleep-image-card--small">
              <Image
                src={getAssetUrl("/images/results/redefining_sleep_invite_her.webp")}
                alt={t("image-alt-small")}
                className="redefining-sleep-image redefining-sleep-image-invite-her"
                width={352}
                height={398}
                unoptimized
              />
            </div>
            <div className="redefining-sleep-invite-card">
              <p className="redefining-sleep-invite-text redefining-sleep-invite-text-desktop">
                {t.rich(`${genderCaptionPath}.invite-body`, {
                bold: (chunks) => (
                    <span className="redefining-sleep-invite-text-bold">{chunks}</span>
                ),
                })}
              </p>
              <p className="redefining-sleep-invite-text redefining-sleep-invite-text-mobile">
                {t.rich(`${genderCaptionPath}.invite-body-mobile`, {
                bold: (chunks) => (
                    <span className="redefining-sleep-invite-text-bold">{chunks}</span>
                ),
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}